// Generate an enrollment config (the push/agent model): mint ONE universal
// enrollment token. The MoonShine installer's own Host/Client/Both selector
// decides what enrolls, so the token is not pre-bound to a role — it can enroll
// one host and one client. Optional hints: a team (host placement) and a user
// (client owner). Ships the per-OS install one-liners + moonshine:// deep link.
import React, { useEffect, useState } from "react";
import { C, F, Btn, Pill } from "../brand/apo";
import { Tabs } from "../brand/sh";
import { api, type FleetUser, type Team, type EnrollTokenResponse } from "../api";
import { detectOs, type DetectedOs } from "../lib/os";

const labelStyle: React.CSSProperties = {
  fontFamily: F.mono,
  fontSize: 10,
  letterSpacing: "0.18em",
  color: C.fgFaint,
  fontWeight: 600,
  textTransform: "uppercase",
  marginBottom: 7,
  display: "block",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  background: C.surfaceHi,
  border: `1px solid ${C.line}`,
  color: C.fgDark,
  fontFamily: F.ui,
  fontSize: 13.5,
  padding: "9px 12px",
  appearance: "auto",
};

function CopyBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <div style={{ position: "relative", background: C.ink, border: `1px solid ${C.line}`, padding: "11px 12px" }}>
      <code style={{ fontFamily: F.mono, fontSize: 12, color: C.glow, wordBreak: "break-all", whiteSpace: "pre-wrap", display: "block", paddingRight: 56 }}>{text}</code>
      <button
        onClick={copy}
        style={{ position: "absolute", top: 8, right: 8, fontFamily: F.mono, fontSize: 10, letterSpacing: "0.1em", color: copied ? C.ink : C.fgDark, background: copied ? C.glow : C.surfaceHi, border: `1px solid ${copied ? C.glow : C.line}`, padding: "4px 9px", cursor: "pointer", textTransform: "uppercase" }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

// The three host targets, in tab order. macOS is a flat .pkg (no install-time
// args) so its one-liner enrolls as a post-install step; Linux has no published
// artifact yet, so the api may return a "not yet published" placeholder.
type InstallOs = "windows" | "macos" | "linux";
const OS_TABS: { id: InstallOs; label: string }[] = [
  { id: "windows", label: "Windows" },
  { id: "macos", label: "macOS" },
  { id: "linux", label: "Linux" },
];

// The browser-detected OS only seeds the default tab; everything stays
// selectable since an operator commonly provisions a host of another OS.
function defaultOsTab(detected: DetectedOs): InstallOs {
  return detected === "unknown" ? "windows" : detected;
}

// Heuristic: the api returns a placeholder one-liner for OSes with no built
// artifact (today: Linux). Surface those with a subtle "preview" pill rather
// than pretending the command works.
function isPreviewCommand(cmd: string | undefined): boolean {
  return !cmd || /not\s*(yet\s*)?published|preview|coming soon/i.test(cmd);
}

// OS-tabbed install block for HOST tokens. Defaults to the detected OS but lets
// the operator switch freely; each tab shows its installCommand one-liner.
function HostInstallTabs({ install }: { install: EnrollTokenResponse["installCommand"] }) {
  const [os, setOs] = useState<InstallOs>(() => defaultOsTab(detectOs()));
  const cmd = install[os];
  const preview = isPreviewCommand(cmd);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <Tabs tabs={OS_TABS} value={os} onChange={(id) => setOs(id as InstallOs)} />
        {preview && (
          <Pill color={C.amber} size="sm">
            Preview
          </Pill>
        )}
      </div>
      {preview && !cmd ? (
        <div style={{ background: C.ink, border: `1px solid ${C.line}`, padding: "11px 12px", fontFamily: F.mono, fontSize: 12, color: C.fgMute, lineHeight: 1.5 }}>
          Installer not yet published for this platform.
        </div>
      ) : (
        <CopyBlock text={cmd} />
      )}
    </div>
  );
}

export function GenerateHostInstall() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<FleetUser[]>([]);
  // Optional placement hints, both carried by one universal token: team applies
  // if the install enrolls a host, user if it enrolls a client. The MoonShine
  // installer's own Host/Client/Both selector decides which actually happens.
  const [teamId, setTeamId] = useState("");
  const [userId, setUserId] = useState("");
  const [result, setResult] = useState<EnrollTokenResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const generate = async (opts: { teamId?: string; userId?: string }) => {
    setLoading(true);
    setError(null);
    try {
      setResult(
        await api.createEnrollToken({
          teamId: opts.teamId || undefined,
          userId: opts.userId || undefined,
        }),
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.getTeams().then((r) => setTeams(r.teams)).catch(() => setTeams([]));
    api.getFleetUsers().then((r) => setUsers(r.users)).catch(() => setUsers([]));
    void generate({});
  }, []);

  return (
    <div>
      <div style={{ fontSize: 13, color: C.fgMute, lineHeight: 1.55, marginBottom: 16 }}>
        Run this on the target machine. The MoonShine installer lets you choose
        Host, Client, or both — this token works for whichever you pick. The
        agent registers itself with Stillhouse and joins the Netbird mesh
        automatically, no address or credentials to type.
      </div>

      <label style={{ display: "block", marginBottom: 16 }}>
        <span style={labelStyle}>Assign host to team (optional)</span>
        <select
          style={selectStyle}
          value={teamId}
          onChange={(e) => {
            setTeamId(e.target.value);
            void generate({ teamId: e.target.value, userId });
          }}
        >
          <option value="">Unassigned</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>

      <label style={{ display: "block", marginBottom: 16 }}>
        <span style={labelStyle}>Bind client to user (optional)</span>
        <select
          style={selectStyle}
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
            void generate({ teamId, userId: e.target.value });
          }}
        >
          <option value="">No user binding</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.displayName} ({u.email})
            </option>
          ))}
        </select>
        <span style={{ display: "block", marginTop: 6, fontFamily: F.mono, fontSize: 10.5, color: C.fgFaint, lineHeight: 1.5 }}>
          {users.length === 0
            ? "No users yet — invite people from Teams & users and they appear here."
            : "When the install includes a client, it's attributed to this user (reaches only their machines in RESTRICTED mode)."}
        </span>
      </label>

      {loading && <div style={{ fontFamily: F.mono, fontSize: 12, color: C.fgMute }}>Generating…</div>}
      {error && <div style={{ fontFamily: F.mono, fontSize: 12, color: C.rose }}>{error}</div>}

      {result && !loading && (
        <React.Fragment>
          <HostInstallTabs install={result.installCommand} />
          <div style={{ marginTop: 16 }}>
            <span style={labelStyle}>Client deep link</span>
            <CopyBlock text={result.deepLink} />
          </div>
          <div style={{ marginTop: 16 }}>
            <span style={labelStyle}>Enrollment token</span>
            <CopyBlock text={result.token} />
          </div>
          {result.msp1 && (
            <div style={{ marginTop: 16 }}>
              <span style={labelStyle}>Mesh enroll key (client)</span>
              <CopyBlock text={result.msp1} />
              <span style={{ display: "block", marginTop: 6, fontFamily: F.mono, fontSize: 10.5, color: C.fgFaint, lineHeight: 1.5 }}>
                Paste into MoonShine → Settings → Mesh to enroll a client device (SSO or setup-key, per the org's mesh mode).
              </span>
            </div>
          )}
          <div style={{ marginTop: 12, fontFamily: F.mono, fontSize: 11, color: C.fgFaint, lineHeight: 1.6 }}>
            One host + one client · expires {new Date(result.expiresAt).toLocaleString()} · the machine appears here the moment it calls home.
          </div>
        </React.Fragment>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
        <Btn ghost onClick={() => void generate({ teamId, userId })}>
          Regenerate token
        </Btn>
      </div>
    </div>
  );
}
